import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';

import { AspectRatio, Box, Flex, SvgIcon as BaseSvgIcon, Text } from 'Components/Base';
import { get as getTheme } from 'Components/Base/constants';
import { useConfig } from 'Context/ConfigContext';

const RatioBox = styled(AspectRatio)`
  border: 1px solid ${getTheme('colors.greyscale.grey4')};
  border-radius: 8px;
  overflow: hidden;
  && > img {
    object-fit: contain;
  }
`;

const IconWrapper = styled((props) => (
  <Flex width={'32px'} height={'32px'} justifyContent={'center'} alignItems={'center'} {...props} />
))`
  background-color: ${({ theme, $bg }) => $bg || theme.colors.coolGrey.grey3};
  cursor: ${({ $cursor }) => $cursor || 'pointer'};

  border-radius: 50%;
`;

const SvgIcon = styled(BaseSvgIcon)`
  color: ${({ theme, $color }) => $color || theme.colors.primary};
  opacity: ${({ $opacity }) => $opacity || 1};
`;

const PageWrapper = styled((props) => <Box px={5} py={1} bg={'coolGrey.grey3'} {...props} />)`
  border-radius: 100px;
`;

const ClearImageGallery = ({ images = [], ...props }) => {
  const [imageIndex, setImageIndex] = useState(0);
  const { colors } = useTheme();
  const _isFirstImage = imageIndex === 0;
  const _isLastImage = imageIndex === images.length - 1;

  const { classroom: classroomConfig } = useConfig();
  const fallbackScreenshotUrl =
    classroomConfig?.clearByOnDemand?.fallbackScreenshotUrl ??
    '/assets/images/defaultCourseThumbnail/empty-thumbnail-ANY.png';
  const handleNextImage = () => {
    if (imageIndex === images.length - 1) return;
    setImageIndex(imageIndex + 1);
  };

  const handlePreviousImage = () => {
    if (imageIndex === 0) return;
    setImageIndex(imageIndex - 1);
  };

  return (
    <Box {...props}>
      <RatioBox ratio={16 / 9}>
        <img
          src={images[imageIndex] ?? fallbackScreenshotUrl}
          alt={`clear-attachment-${imageIndex}`}
          onError={(e) => {
            console.error(`${e.target.src} 404 not found`);
            e.target.onerror = null;
            e.target.src = fallbackScreenshotUrl;
          }}
        />
      </RatioBox>
      <Flex mt={3} justifyContent={'center'} alignItems={'center'}>
        <IconWrapper
          onClick={handlePreviousImage}
          $bg={_isFirstImage && colors.coolGrey.grey2}
          $cursor={_isFirstImage && 'not-allowed'}
        >
          <SvgIcon
            icon={SvgIcon.ICON_LIST.BACK}
            size={24}
            $color={_isFirstImage && colors.text}
            $opacity={_isFirstImage && 0.3}
          />
        </IconWrapper>
        <PageWrapper mx={4}>
          <Text>
            {imageIndex + 1}/{images.length}
          </Text>
        </PageWrapper>
        <IconWrapper
          onClick={handleNextImage}
          $bg={_isLastImage && colors.coolGrey.grey2}
          $cursor={_isLastImage && 'not-allowed'}
        >
          <SvgIcon
            icon={SvgIcon.ICON_LIST.NEXT}
            size={24}
            $color={_isLastImage && colors.text}
            $opacity={_isLastImage && 0.3}
          />
        </IconWrapper>
      </Flex>
    </Box>
  );
};

ClearImageGallery.propTypes = {
  images: PropTypes.array,
};

export default ClearImageGallery;
